import React from 'react'
import { PuffLoader as Loader } from 'react-spinners'
import { ExportActionMutator, FotaMutator, SetEditableActionMutator, KeysLinksMutator, SaleJobSpec } from '@leverege/recovr-plugins'
import { LoadingView } from '@leverege/molten/views'
import { GlobalState } from '@leverege/ui-redux'
import Fota from '@leverege/api-ui-fota-management'

import Theme from './theme'
import ActionPersonaMutator from './mutators/ActionPersonaMutator'
import ExportMutator from './mutators/ExportMutator'
import LimitToRolesMutator from './mutators/LimitToRolesMutator'
import searchUsersMutator from './mutators/SearchUsersMutator'
import './global-style.css'

const adminHandles = ( context ) => {
  // hide for all but steve on mobile
  // const { clientProps : { mobile } } = context
  const hasDefault = !!GlobalState.get()?.ui?.personas?.some( p => p.name === 'default' )
  // only non-custom persona user's when on mobile
  return hasDefault
}

const isDev = process.env.IS_DEV === 'true'

function create( env = process.env ) {
  return {
    uiName : process.env.userManagementUiName || 'deviceManagement',
    r4kEnabled : process.env.R4K_ENABLED,
    molten : {
      splashClass : Loading
    },
    theme : {
      theme : Theme,

      // --------------------------------------------------------------------------------------------------------
      // The internal support tool (recovr-device-management-ui) uses the internal support tool builder theme,
      // which is inherited from the standard recovrd theme, and identical other than logos.
      // The ID of a ui builder project is not the same as its url, so both are listed here for convenience
      // UI builder project id: 0xaE6wWZIk9vvgGV8C4CiH
      // Link to UI Builder project: https://builder.leverege.com/appearance/4BWzO0piVlWay5GMCTSjmp/elements/Text
      // --------------------------------------------------------------------------------------------------------
      appearanceProjectId : process.env.APPEARANCE_PROJECT_ID || '0xaE6wWZIk9vvgGV8C4CiH',
      useActiveTheme : isDev
    },
    maps : {
      apiKey : process.env.GOOGLE_MAPS_API_KEY,
      mapId : process.env.GOOGLE_MAPS_MAP_ID,
    },
    api : {
      host : process.env.IMAGINE_HOST || 'http://localhost:8181',
      systemId : process.env.SYSTEM_ID,
      projectId : process.env.PROJECT_ID
    },
    googleAnalytics : { 
      trackerId : 'G-XLWE87MLHT',
    },
    sentry : {
      dsn : 'https://c09a34ec2259abb7e81114376cfd1dc2@o4505749264072704.ingest.sentry.io/4505761647034368',
      enabled : !process.env.WEBPACK_SERVE,
      environment : process.env.DEPLOYMENT_TARGET,
      replaysSessionSampleRate : isDev ? 0 : 0.01,
      replaysOnErrorSampleRate : isDev ? 0.1 : 0.5
    },
    DetailTypes : [],
    plugins : {
      // LoadConfig will use these and call Config.set()
      config : {
        'molten.dataViewers.layout.RenameLayoutAction' : {
          handles : adminHandles
        },
        'molten.dataViewers.layout.DeleteLayoutAction' : {
          handles : adminHandles
        },
        'molten.dataViewers.layout.SetLayoutAsDefaultAction' : {
          handles : adminHandles
        },
        BetaSwitch : {
          enabled : env.BETA_ENABLED === 'true' || process.env.BETA_ENABLED === 'true',
          isBetaSite : env.BETA_IS_BETA === 'true' || process.env.BETA_IS_BETA === 'true',
          betaHost : env.BETA_HOST_BETA || process.env.BETA_HOST_BETA,
          normalHost : env.BETA_HOST_NORMAL || process.env.BETA_HOST_NORMAL,
          feedbackLink : 'https://airtable.com/shrP9Hizjjl5bSabx',
          releaseNotesLink : 'https://releases.recovr.biz/release-notes/internal-support-beta-2-0-0'
        },
        VehicleSettings : {
          usageTypeEnum : env.VEHICLE_USAGE_TYPE_ENUM || process.env.VEHICLE_USAGE_TYPE_ENUM
        },
        RecovrServer : {
          billingModule : process.env.RECOVR_BILLING_MODULE || 'recovr'
        },
        LayoutViewerSelector : {
          allowEditing : 'default'
        },
        GroupScreen : {
          defaultPerPage : 200
        },
        'VehicleNetwork.dealer' : {
          id : process.env.DEALER_VEHICLE_NETWORK_ID || process.env.VEHICLE_NETWORK_ID || 'prd-auto-vehicles',
          aliasKey : 'deviceId'
        },
        'VehicleNetwork.consumer' : {
          id : process.env.CONSUMER_VEHICLE_NETWORK_ID || 'prd-customer-vehicles',
          aliasKey : 'deviceId'
        },
        ConsumerLocation : {
          id : process.env.CONSUMER_LOCATION_ID
        },
        LocatorNetwork : {
          id : process.env.LOCATOR_NETWORK_ID || 'prd-recovr-tracker',
          aliasKey : 'serialNumber'
        },
        ParentSetter : {
          listLimit : 600
        },
        ResponsiveDataViewer : {
          minWidth : 430
        },
        LoginScreen : {
          oidcClientFilter : [ { regex : '^((?!carmax).)*$' } ], // hide carmax OIDC
          // userText : 'Account Email'
          loginOpts : {
            options : {
              loginByCodeEnabled : false
            },
            classNames : {
              forgotClassName : 'forgotPassword' 
            }
          },
        },
        AccountScreen : {
          redirectUrl : '/account/profile'
        },
        LoadingScreen : {
          spinner : Loader,
          props : { size : 70 } // error in lib -this one is off center
        },
        LoadingView : {
          spinner : Loader,
          props : { size : 30 } // error in lib -this one is off center
        },
        AttributeInlineEditor : {
          inProgress : LoadingView.Inline
        },
        JobsScreen : {
          navIcon : 'https://recovr-ui-assets.storage.googleapis.com/blueprintNavIcons/jobs-nav.svg',
          titleIcon : 'https://recovr-ui-assets.storage.googleapis.com/job-title.png',
        },
        Filtering : {
          attributeNameMap : {
            'vehicle.color' : null,
            'vehicle.swatchAndColor' : 'filter.recovr.colorCustom',
            'vehicle.calculatedAlerts' : 'filter.calculatedAlerts',
            'locator.operationStateChangeStatus' : null,
            'vehicle.locatorPaired' : 'filter.locatorPaired',
            'locator.batteryAlerts' : 'filter.batteryAlerts',
            'locator.events.resetRequestState' : 'filter.aliasEquals.resetRequestState'
          },
          aggregationSize : 500
        },
        ZoneVehicleAdapter : {
          accuracyMultiplier : 5
        },
        LostScreen : {
          image : [
            'https://molten-ui-assets.storage.googleapis.com/404-UFO.png',
            'https://molten-ui-assets.storage.googleapis.com/404-Earth.png',
            'https://molten-ui-assets.storage.googleapis.com/404-Page.png',
            'https://molten-ui-assets.storage.googleapis.com/404-Ghost.png'
          ]
        },
        ResourceUsers : {
          screenIcon : 'https://recovr-ui-assets.storage.googleapis.com/users-title.png',
          navIcon : 'https://recovr-ui-assets.storage.googleapis.com/blueprintNavIcons/users-nav.svg',
          userSelectorUsernameLabel : 'Account Email',
          userSelectorUsernameHint : 'Enter Account Email',
          userSelectorUsernameInstructions : "Enter user's account email and hit enter or next to continue.",
          createUsernameLabel : 'Account Email',
          createUsernameHint : 'Enter Account Email',
          allowCreateUser : false,
          userSearchField : 'username',
          oidcClientFilter : [ { regex : '^((?!carmax).)*$' } ], // hide carmax OIDC
        },
        InactivityScreen : {
          title : React.createElement( 'img', {
            src : 'https://recovr-ui-assets.storage.googleapis.com/logo-dark.png',
            width : 150
          } ),
          image : [
            'https://storage.googleapis.com/recovr-ui-assets/broken-hourglass.png',
            'https://storage.googleapis.com/recovr-ui-assets/sleepy-car.png'
          ]
        },
        Main : {
          userIcon : 'https://recovr-ui-assets.storage.googleapis.com/user.png'
        },
        VoidSale : {
          saleLocationParentPath : env.SALE_LOCATION_PARENT_PATH || process.env.SALE_LOCATION_PARENT_PATH
        },
        OperationStates : {
          operationStates : {
            1 : 'Shipment',
            2 : 'Factory Default',
            8 : 'Available',
            9 : 'Paired',
            10 : 'Sold',
            11 : 'Active Theft Recovery',
            12 : 'Disabled'
          }          
        },
        GatewayNetwork : {
          aliasKey : 'serialNumber'
        },
        PositionRequest : {
          pairedPositionSupportedConfigs : process.env.PAIRED_POSITION_SUPPORTED_CONFIGS?.split( ',' ) || [ ]
        },
        'link.saleJob.GroupScreen' : {
          layout : {
            group : [ 'Inventory' ],
            groupNames : [ 'Inventory' ],
            sort : 'zzz',
          }
        },
        LocalCanResolver : {
          compareModeActive : false,
        }
      },
      excludes : [
        // vehicle locator exclusion
        // 'blueprint.company.locations.vehicles.locator.ItemScreen',
        // 'blueprint.link.company.locations.vehicles.locator.GroupScreen',
        // 'blueprint.company.vehicles.locator.ItemScreen',
        // 'blueprint.link.company.vehicles.locator.GroupScreen',

        'blueprint.action.location.sales.UpdateItem',
        'blueprint.action.company.locations.sales.UpdateItem',
        'blueprint.action.company.sales.UpdateItem',

        'blueprint.location.vehicles.locator.ItemScreen',
        'blueprint.link.location.vehicles.locator.GroupScreen',

        // Hide pairing at the company level
        'recovr.company.vehicles.locator.pair',
        'recovr.vehicle.locator.pair',
        // Hide create new payment action
        'blueprint.action.payment.CreateItem',
        'blueprint.action.bill.payments.CreateItem',

        // Hide create new parent jobs actions
        'blueprint.action.parentLogUploadJob.CreateItem',
        'blueprint.action.company.locations.parentLogUploadJob.CreateItem',
        'blueprint.action.company.parentLogUploadJob.CreateItem',
        'blueprint.action.locations.parentLogUploadJob.CreateItem',

        // Hide create new child jobs actions
        'blueprint.action.childLogUploadJob.CreateItem',
        'blueprint.action.parentLogUploadJob.childLogUploadJobs.CreateItem',
        'blueprint.action.company.locations.childLogUploadJob.CreateItem',
        'blueprint.action.company.childLogUploadJob.CreateItem',
        'blueprint.action.locations.childLogUploadJob.CreateItem',
        'blueprint.action.company.locations.parentLogUploadJob.childLogUploadJobs.CreateItem',
        'blueprint.action.locations.parentLogUploadJob.childLogUploadJobs.CreateItem',

        // case vehicle exclusion
        // 'blueprint.company.locations.cases.vehicle.ItemScreen',
        // 'blueprint.link.company.locations.cases.vehicle.GroupScreen',
        // 'blueprint.company.cases.vehicle.ItemScreen',
        // 'blueprint.link.company.cases.vehicle.GroupScreen',
        // 'blueprint.location.cases.vehicle.ItemScreen',
        // 'blueprint.link.location.cases.vehicle.GroupScreen',

        'recovr.homeLink',
        'leverege.fotaApi.link.deviceGroup',
        'leverege.fotaApi.link.device',
        'leverege.fotaApi.link.firmwareRelease'
      ],
      mutators : [
        // array of functions( type, plugin ) that can return the plugin to continue,
        // null to exclude it, or a plugin to replace it.
        ActionPersonaMutator,
        ExportMutator,
        LimitToRolesMutator,
        ExportActionMutator,
        FotaMutator,
        SetEditableActionMutator,
        KeysLinksMutator,
        searchUsersMutator,
      ],
    },
    fotaMutator : {
      'options.imagine.groupBlueprintId' : {
        objectType : Fota.Constants.Types.deviceType,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'options.imagine.groupOperationHoursPath' : {
        objectType : Fota.Constants.Types.deviceType,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'options.imagine.subGroupName' : {
        objectType : Fota.Constants.Types.deviceType,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'options.imagine.systemId' : {
        objectType : Fota.Constants.Types.deviceType,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'options.imagine.writeToGroup' : {
        objectType : Fota.Constants.Types.deviceType,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'firmwareUpgradeOptions.percentDevicesToGetFirmwareApplication' : {
        objectType : Fota.Constants.Types.deviceGroup,
        patch : [ { path : 'source/editable', value : false } ]
      },
      imagineGroupId : {
        objectType : Fota.Constants.Types.deviceGroup,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'firmwareUpgradeOptions.useOperationHoursFromImagineGroup' : {
        objectType : Fota.Constants.Types.deviceGroup,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'firmwareUpgradeOptions.useDeviceTypeUpgradePaths' : {
        objectType : Fota.Constants.Types.deviceGroup,
        patch : [ { path : 'source/editable', value : false } ]
      },
      'leverege.fotaApi.deviceType.metadata' : {
        objectType : Fota.Constants.Types.deviceType,
        patch : [ { path : 'source/editable', value : false } ]
      },
    },
    actions : {
      'molten.dataViewers.list.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.table.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.screens.group.search.SearchSettings' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.details.EditDetailsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.map.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.history.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.aggregations.ContainerEditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.aggregations.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.DefaultLayoutAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.SaveAsLayoutAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.ResetLayoutAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.SaveLayoutAction' : {
        hideForNonAdmin : true
      },
      'link.desktop.Settings' : {
        hideForNonAdmin : true
      },
      'link.mobile.Settings' : {
        hideForNonAdmin : true
      },
      'dashboard.ToggleEditingAction' : {
        hideForNonAdmin : true
      },
      'layout.AdvancedSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.layout.EditLayoutAction' : {
        hideForNonAdmin : true
      },
      'recovr.locator.CancelReset' : {
        limitToRoles : [ 
          'internalAdmin',
          'internalEngineer', // Engineer
        ]
      },
      'recovr.locator.Reset' : {
        limitToRoles : [ 
          'internalAdmin',
          'internalEngineer', // Engineer
        ]
      },
      'recovr.changeVehicleOwnership' : {
        limitToRoles : [
          'internalAdmin',
          'internalSupportViewer',
          'internalEditor',
        ]
      }
    },
    openApiOverrides : {
      SaleJobService : {
        'servers/0/url' : process.env.IMAGINE_HOST || 'http://localhost:8181'
      }
    },
    openApis : [
      SaleJobSpec
    ],
  }
}

function Loading( props ) {
  return React.createElement( 'div', null, 'Loading Molten...' )
}

export default {
  create
}
